/**
 * The CP second-factor page (kernel apps, WS-9b, `design-docs/WS-9.md` section 3.6, locked
 * decision 5).
 *
 * Every CP login ends in a passkey wait. A password login gets the wait in its answer; a Google or
 * GitHub login lands on `/login/second-factor#sf=<token>`. `CpAdminApp`'s `SecondFactorGate` takes
 * the fragment with `takeSecondFactorFragment`, hands the token to `useAuth`, and renders this page
 * while the wait lasts. The page runs the ceremony through `onComplete` and then goes where the
 * server says: the console for a session, the console callback or the CLI listener for a redirect.
 *
 * Security rules (`design-docs/WS-9.md` section 5): the page never holds the token, only the
 * challenge (F-02). The fragment leaves the address bar before any request, and only the
 * 43-character grammar passes (F-11). A redirect goes only to the console callback or to
 * `http://localhost` (F-12).
 *
 * Stage 5 (WS-31b, R-150, R-152): an `authenticate` wait whose `recoveryMethods` list is not empty
 * shows "Use another way", which opens `SecondFactorRecoveryCard` (`second-factor-recovery.tsx`). A
 * passed proof turns the wait into a `register` wait with `recovered: true`, and the register arm
 * says so. A verify whose result carries ten recovery codes shows `RecoveryCodesCard` first; the
 * session or the redirect goes on only after the operator saved the codes.
 *
 * The redirect holds a single-use authorization code that cp-auth-api's `OAuthService` keeps for
 * 60 s from the mint (`AUTH_CODE_TTL_SECONDS`), and the server mints it before the codes show
 * (WS-31b-R1). So a redirect to the console's own callback is exchanged at once, while the codes
 * show, through `onExchangeCode`; the Close of the codes then opens the console. A redirect to the
 * command-line listener cannot be exchanged here: the codes card names the clock, and a Close after
 * `SECOND_FACTOR_REDIRECT_CODE_BUDGET_MS` shows `SECOND_FACTOR_REDIRECT_EXPIRED_TEXT` in place of a
 * dead redirect.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { isAllowedSecondFactorRedirect, secondFactorRefusalOf } from '../platform/auth/auth.api';
import type { RecoveryProof, SecondFactorChallenge, SecondFactorResult } from '../platform/auth/useAuth';
import { takeReturnTo as takeReturnToDefault } from '../platform/auth/return-to';
import { displayMessage } from '../platform/errors';
import { AuthCardFrame, AuthPendingState } from '../shell/AuthCardFrame';
import { Button } from '../shell/Button';
import { FormField } from '../shell/FormField';
import { isUserCancellation } from './LoginPage';
import { RecoveryCodesCard, SecondFactorRecoveryCard, type RecoveryView } from './second-factor-recovery';

/** The console path of the second-factor page. */
export const SECOND_FACTOR_PATH = '/login/second-factor';

/** The grammar of a CP second-factor token in a `#sf=` fragment: 43 base64url characters (32 bytes). */
export const SECOND_FACTOR_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;

/** The text of a wait that the server ended, or whose expiry passed. */
export const SECOND_FACTOR_EXPIRED_TEXT = 'This sign-in step expired';

/** The text of a page that has no wait: a `#sf=` link with no valid token. */
export const SECOND_FACTOR_INVALID_LINK_TEXT = 'This sign-in link is not valid';

/** The text of `operator_inactive`. */
export const SECOND_FACTOR_INACTIVE_TEXT = 'This account cannot sign in.';

/** The text of `verification_failed`. The wait stays, so the operator can try again. */
export const SECOND_FACTOR_NOT_VERIFIED_TEXT = 'The passkey did not verify. Try again.';

/** The text of a redirect that `isAllowedSecondFactorRedirect` refuses. */
export const SECOND_FACTOR_REFUSED_REDIRECT_TEXT = 'The sign-in answer named an address that the console does not open.';

/**
 * How long, from the verify answer, the page lets a command-line redirect wait behind the codes
 * card. cp-auth-api keeps the authorization code for 60 s (`AUTH_CODE_TTL_SECONDS` of
 * `OAuthService`), counted from the mint before the answer left. The 10 s margin covers the trip,
 * the listener's own exchange and the clock skew between the two counts.
 */
export const SECOND_FACTOR_REDIRECT_CODE_BUDGET_MS = 50_000;

/** The notice of the codes card while a command-line sign-in waits behind it. */
export const SECOND_FACTOR_REDIRECT_CLOCK_TEXT =
  'Save the codes and close them within 50 seconds. The command-line sign-in waits no longer than that.';

/** The text of a command-line redirect whose authorization code expired while the codes showed. */
export const SECOND_FACTOR_REDIRECT_EXPIRED_TEXT =
  'The command-line sign-in waited more than 50 seconds and expired. Your saved recovery codes stay valid. Start the sign-in again.';

/** The text of a console callback whose exchange failed while the codes showed. */
export const SECOND_FACTOR_EXCHANGE_FAILED_TEXT =
  'The sign-in did not complete. Your saved recovery codes stay valid. Sign in again.';

/** The console path of the OAuth callback that `OAuthCallbackPage` serves. */
const CONSOLE_CALLBACK_PATH = '/auth/oauth/callback';

/**
 * The authorization code of a redirect to the console's own OAuth callback, or `null`.
 *
 * Callers: `SecondFactorPage`, for a verify result that carries recovery codes. A URL that
 * `isAllowedSecondFactorRedirect` refuses, a URL on another origin or path (the command-line
 * listener on `http://localhost`), and a callback with no `code` or an empty one answer `null`. Pure.
 *
 * @param url the `redirectUrl` of the verify answer.
 * @param consoleOrigin `window.location.origin`.
 */
export function consoleCallbackCodeOf(url: string, consoleOrigin: string): string | null {
  if (!isAllowedSecondFactorRedirect(url, consoleOrigin)) return null;
  const parsed = new URL(url);
  if (parsed.origin !== consoleOrigin || parsed.pathname !== CONSOLE_CALLBACK_PATH) return null;
  const code = parsed.searchParams.get('code');
  return code === null || code === '' ? null : code;
}

/** The subtitle of the `register` arm after a passed recovery proof. */
export const SECOND_FACTOR_RECOVERED_TEXT = 'The recovery proof passed. Register a new passkey on this device to finish the sign-in.';

/**
 * Take the `sf` token out of the fragment, and take the fragment out of the address bar.
 *
 * A hash with any text makes it call `replace(pathname + search)` first, so the fragment leaves the
 * address bar and the history entry before any request. A browser sends no fragment to a server or
 * in `Referer`.
 *
 * The caller passes the router's own location and a `replace` that runs the router's
 * `navigate(path, { replace: true })`. A raw `history.replaceState` fires no `popstate`, so
 * `BrowserRouter` keeps the old location, hash and token included, in its React state and in every
 * `useLocation()` reader (F-02). The raw call also writes over the `{ usr, key, idx }` state that
 * the router keeps in the history entry.
 *
 * @param location the router-relative location: `useLocation()` under `BrowserRouter`.
 * @param replace replaces the current history entry with a router-relative path.
 * @returns the `sf` value when `SECOND_FACTOR_TOKEN_PATTERN` accepts it, else `null`: no hash, no
 *   `sf` key, a short or long token, or a character outside the grammar.
 */
export function takeSecondFactorFragment(
  location: Pick<Location, 'hash' | 'pathname' | 'search'>,
  replace: (path: string) => void,
): string | null {
  const hash = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash;
  if (hash === '') return null;
  replace(location.pathname + location.search);
  const token = new URLSearchParams(hash).get('sf');
  return token !== null && SECOND_FACTOR_TOKEN_PATTERN.test(token) ? token : null;
}

export interface SecondFactorPageProps {
  /** The wait, or `null` for a `#sf=` link that carried no valid token. */
  readonly challenge: SecondFactorChallenge | null;
  /** `useAuth.readSecondFactorCeremony`: posts the options of a resumed wait. */
  readonly onReadCeremony: () => Promise<void>;
  /** `useAuth.abandonSecondFactor`: ends the wait with no request. */
  readonly onAbandon: () => void;
  /** `useAuth.completeSecondFactor`: runs the ceremony and the verify. */
  readonly onComplete: (deviceName: string | null) => Promise<SecondFactorResult>;
  /** `useAuth.proveRecovery`: posts one recovery proof (WS-31b). */
  readonly onProveRecovery: (proof: RecoveryProof) => Promise<void>;
  /** `useAuth.sendRecoveryEmail`: asks for the recovery mail (WS-31b). */
  readonly onSendRecoveryEmail: () => Promise<void>;
  /**
   * `useAuth.loginWithOAuthCode`: exchanges the authorization code of a console callback redirect
   * while the recovery codes show (WS-31b-R1). It adopts the session and keeps the wait.
   */
  readonly onExchangeCode: (code: string) => Promise<void>;
  /** Takes the return-to stash once. Defaults to `takeReturnTo` of `return-to.ts`. */
  readonly takeReturnTo?: () => string | null;
  /** The clock in epoch milliseconds. Defaults to `Date.now`. */
  readonly now?: () => number;
  /** Opens a URL outside the router. Defaults to `window.location.assign`. */
  readonly navigateExternal?: (url: string) => void;
}

function defaultNow(): number {
  return Date.now();
}

function defaultNavigateExternal(url: string): void {
  window.location.assign(url);
}

/** A failure that ends the wait for the operator: the page then offers "Back to sign in" alone. */
type Ended = 'expired' | 'inactive' | 'invalid-link' | 'refused-redirect' | 'redirect-expired' | 'exchange-failed';

const ENDED_TEXT: Readonly<Record<Ended, string>> = {
  expired: SECOND_FACTOR_EXPIRED_TEXT,
  inactive: SECOND_FACTOR_INACTIVE_TEXT,
  'invalid-link': SECOND_FACTOR_INVALID_LINK_TEXT,
  'refused-redirect': SECOND_FACTOR_REFUSED_REDIRECT_TEXT,
  'redirect-expired': SECOND_FACTOR_REDIRECT_EXPIRED_TEXT,
  'exchange-failed': SECOND_FACTOR_EXCHANGE_FAILED_TEXT,
};

/** A verify result with recovery codes, held while the codes card shows. */
interface Issued {
  readonly result: SecondFactorResult;
  readonly codes: readonly string[];
  /** `now()` when the verify answered. */
  readonly issuedAt: number;
  /** The exchange of a console callback: settles `true` for a session, `false` for a failure. */
  readonly exchange: Promise<boolean> | null;
}

/**
 * The second-factor wait as a card.
 *
 * - `challenge` `null`: `SECOND_FACTOR_INVALID_LINK_TEXT` and "Back to sign in".
 * - `ceremony` `null`: it calls `onReadCeremony` once and shows the pending state. A read that
 *   fails with no terminal refusal (a network fault, a 429, a 5xx, a body the contract refuses)
 *   shows the `displayMessage` of the failure, "Try again" and "Back to sign in". "Try again" reads
 *   once more, or shows `SECOND_FACTOR_EXPIRED_TEXT` when `now()` is past `expiresAt`.
 * - `authenticate`: the button "Use your passkey".
 * - `register`: a required "Device name" field and the button "Register a passkey" (I-9).
 *
 * A `session` result navigates to `takeReturnTo() ?? '/'` with `replace`. When that session owes a
 * password change, `CpAdminApp` branch 3 renders the rotation form at the destination and stashes it
 * again (`StashAndChangePassword`), so the operator lands there after the rotation. A `redirect`
 * result that `isAllowedSecondFactorRedirect` accepts for `window.location.origin` shows the leave
 * state, calls `navigateExternal` and takes no stash; another redirect shows
 * `SECOND_FACTOR_REFUSED_REDIRECT_TEXT`.
 * The leave state and every ended arm win over a `challenge` that turns `null`, so the page never
 * shows `SECOND_FACTOR_INVALID_LINK_TEXT` after a verify.
 *
 * Recovery (WS-31b): an `authenticate` wait with a non-empty `recoveryMethods` shows "Use another
 * way" under the passkey button; it opens `SecondFactorRecoveryCard`, which can go back to the
 * passkey. A `register` wait with `recovered: true` shows `SECOND_FACTOR_RECOVERED_TEXT` as its
 * subtitle. A result with `recoveryCodes` shows `RecoveryCodesCard`, and its Close goes on:
 * - a `session`: it calls `onAbandon`, because `useAuth` keeps that wait open while the codes show,
 *   and finishes the result as above;
 * - a redirect to the console callback (`consoleCallbackCodeOf`): the page called `onExchangeCode`
 *   when the verify answered. The Close shows the pending state until the exchange settles. A
 *   session then calls `onAbandon` and navigates to `takeReturnTo() ?? '/'` with `replace`; a
 *   failure shows `SECOND_FACTOR_EXCHANGE_FAILED_TEXT`;
 * - another allowed redirect (the command-line listener): the card shows
 *   `SECOND_FACTOR_REDIRECT_CLOCK_TEXT`. A Close more than `SECOND_FACTOR_REDIRECT_CODE_BUDGET_MS`
 *   after the verify shows `SECOND_FACTOR_REDIRECT_EXPIRED_TEXT` and opens nothing; an earlier one
 *   finishes the result as above;
 * - a refused redirect: `SECOND_FACTOR_REFUSED_REDIRECT_TEXT`.
 *
 * Focus: every card that replaces another moves focus to its title (`autoFocusTitle`), and each
 * arm carries its own `key`, so a swap between two arms mounts a new frame and fires its focus.
 *
 * A cancelled prompt shows no error. `token_invalid`, or `now()` past `expiresAt`, shows
 * `SECOND_FACTOR_EXPIRED_TEXT`. `operator_inactive` shows `SECOND_FACTOR_INACTIVE_TEXT`.
 * `verification_failed` shows `SECOND_FACTOR_NOT_VERIFIED_TEXT` and keeps the controls. Other
 * failures show `displayMessage`. Every arm that ends the wait shows "Back to sign in", which calls
 * `onAbandon` and then navigates to `/login`.
 */
export function SecondFactorPage({
  challenge,
  onReadCeremony,
  onAbandon,
  onComplete,
  onProveRecovery,
  onSendRecoveryEmail,
  onExchangeCode,
  takeReturnTo = takeReturnToDefault,
  now = defaultNow,
  navigateExternal = defaultNavigateExternal,
}: SecondFactorPageProps): React.JSX.Element {
  const navigate = useNavigate();
  const [deviceName, setDeviceName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ended, setEnded] = useState<Ended | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [view, setView] = useState<RecoveryView | 'passkey'>('passkey');
  const [issued, setIssued] = useState<Issued | null>(null);
  const [finishing, setFinishing] = useState(false);
  const readRef = useRef(false);

  const ceremony = challenge?.ceremony ?? null;
  const mustRead = challenge !== null && ceremony === null;

  /** Map a failure of the read or the verify to the page's arms. */
  function showFailure(cause: unknown): void {
    if (isUserCancellation(cause)) return;
    const refusal = secondFactorRefusalOf(cause);
    if (refusal === 'token_invalid') setEnded('expired');
    else if (refusal === 'operator_inactive') setEnded('inactive');
    else if (refusal === 'verification_failed') setError(SECOND_FACTOR_NOT_VERIFIED_TEXT);
    else setError(displayMessage(cause, 'The second sign-in step failed.'));
  }

  useEffect(() => {
    // ⛔ Once per page, under StrictMode too: the ref is set before the request leaves.
    if (!mustRead || readRef.current) return;
    readRef.current = true;
    onReadCeremony().catch(showFailure);
  });

  async function complete(active: SecondFactorChallenge): Promise<void> {
    if (now() > active.expiresAt) {
      setEnded('expired');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await onComplete(active.ceremony === 'register' ? deviceName : null);
      const codes = result.recoveryCodes;
      if (codes === undefined) finish(result);
      else setIssued({ result, codes, issuedAt: now(), exchange: startExchange(result) });
    } catch (cause) {
      showFailure(cause);
    } finally {
      setBusy(false);
    }
  }

  /** Go where a completed second factor says: the console for a session, the redirect otherwise. */
  function finish(result: SecondFactorResult): void {
    if (result.kind === 'session') {
      void navigate(takeReturnTo() ?? '/', { replace: true });
    } else if (isAllowedSecondFactorRedirect(result.redirectUrl, window.location.origin)) {
      setLeaving(true);
      navigateExternal(result.redirectUrl);
    } else {
      setEnded('refused-redirect');
    }
  }

  /**
   * Exchange the code of a console callback redirect now, before the operator reads the codes:
   * the code lives 60 s from the mint (WS-31b-R1). `null` for a session or any other redirect.
   */
  function startExchange(result: SecondFactorResult): Promise<boolean> | null {
    if (result.kind === 'session') return null;
    const code = consoleCallbackCodeOf(result.redirectUrl, window.location.origin);
    if (code === null) return null;
    return onExchangeCode(code).then(
      () => true,
      () => false,
    );
  }

  /** The operator saved the issued codes: go on as the docblock of the page lists. */
  async function codesSaved(entry: Issued): Promise<void> {
    const { result, issuedAt, exchange } = entry;
    setIssued(null);
    if (result.kind === 'session') {
      onAbandon();
      finish(result);
      return;
    }
    if (exchange !== null) {
      setFinishing(true);
      if (!(await exchange)) {
        setEnded('exchange-failed');
        return;
      }
      onAbandon();
      void navigate(takeReturnTo() ?? '/', { replace: true });
      return;
    }
    if (
      isAllowedSecondFactorRedirect(result.redirectUrl, window.location.origin)
      && now() - issuedAt > SECOND_FACTOR_REDIRECT_CODE_BUDGET_MS
    ) {
      setEnded('redirect-expired');
      return;
    }
    finish(result);
  }

  /** Read the ceremony once more after a failed read, unless the wait has passed its expiry. */
  function retryRead(active: SecondFactorChallenge): void {
    if (now() > active.expiresAt) {
      setEnded('expired');
      return;
    }
    setError(null);
    onReadCeremony().catch(showFailure);
  }

  function backToSignIn(): void {
    onAbandon();
    void navigate('/login', { replace: true });
  }

  const backButton = (
    <Button size="md" onClick={backToSignIn} data-testid="second-factor-back">
      Back to sign in
    </Button>
  );

  function endedCard(arm: Ended): React.JSX.Element {
    return (
      <AuthCardFrame
        title="Second sign-in step"
        notice={{ verdict: 'bad', message: ENDED_TEXT[arm] }}
        autoFocusTitle
        testId="second-factor"
      >
        {backButton}
      </AuthCardFrame>
    );
  }

  if (leaving) return <AuthPendingState label="Opening the next step of the sign-in…" testId="second-factor-leaving" />;
  if (ended !== null) return endedCard(ended);
  if (finishing) return <AuthPendingState label="Finishing the sign-in…" testId="second-factor-finishing" />;
  if (issued !== null) {
    const waitsOnClock =
      issued.exchange === null
      && issued.result.kind === 'redirect'
      && isAllowedSecondFactorRedirect(issued.result.redirectUrl, window.location.origin);
    return (
      <RecoveryCodesCard
        codes={issued.codes}
        notice={waitsOnClock ? SECOND_FACTOR_REDIRECT_CLOCK_TEXT : undefined}
        onSaved={() => void codesSaved(issued)}
      />
    );
  }
  if (challenge === null) return endedCard('invalid-link');
  if (ceremony === null) {
    if (error === null) return <AuthPendingState label="Reading the sign-in step…" testId="second-factor-pending" />;
    return (
      <AuthCardFrame
        title="Second sign-in step"
        notice={{ verdict: 'bad', message: error }}
        autoFocusTitle
        testId="second-factor"
      >
        <Button tone="primary" size="md" onClick={() => retryRead(challenge)} data-testid="second-factor-retry">
          Try again
        </Button>
        {backButton}
      </AuthCardFrame>
    );
  }

  const notice = error === null ? undefined : { verdict: 'bad' as const, message: error };
  const methods = challenge.recoveryMethods;
  if (ceremony === 'authenticate' && view !== 'passkey' && methods.length > 0) {
    return (
      <SecondFactorRecoveryCard
        methods={methods}
        view={view}
        onView={(next) => {
          setError(null);
          setView(next);
        }}
        onProve={onProveRecovery}
        onSendEmail={onSendRecoveryEmail}
        onEnded={setEnded}
        expired={() => now() > challenge.expiresAt}
      />
    );
  }
  if (ceremony === 'authenticate') {
    return (
      <AuthCardFrame
        key="authenticate"
        title="Second sign-in step"
        subtitle="Confirm the sign-in with the passkey of this account."
        notice={notice}
        autoFocusTitle
        testId="second-factor"
      >
        <Button tone="primary" size="md" disabled={busy} onClick={() => void complete(challenge)} data-testid="second-factor-authenticate">
          Use your passkey
        </Button>
        {methods.length === 0 ? null : (
          <Button size="md" tone="ghost" disabled={busy} onClick={() => setView('choose')} data-testid="second-factor-another-way">
            Use another way
          </Button>
        )}
      </AuthCardFrame>
    );
  }
  return (
    <AuthCardFrame
      key="register"
      title="Second sign-in step"
      autoFocusTitle
      subtitle={
        challenge.recovered
          ? SECOND_FACTOR_RECOVERED_TEXT
          : 'This account has no passkey. Register one on this device to finish the sign-in.'
      }
      notice={notice}
      testId="second-factor"
    >
      <FormField
        label="Device name"
        name="deviceName"
        value={deviceName}
        onChange={setDeviceName}
        testId="second-factor-device-name"
        required
        placeholder="MacBook Touch ID"
      />
      <Button
        tone="primary"
        size="md"
        disabled={busy || deviceName.trim() === ''}
        onClick={() => void complete(challenge)}
        data-testid="second-factor-register"
      >
        Register a passkey
      </Button>
    </AuthCardFrame>
  );
}
